import { Injectable, signal } from '@angular/core';
import { ApiService } from './api.service'; 
import {
  ComponentSummary, ComponentDetail, ComponentType, SafetyGoal, AnalysisRow,
  FailureEffectOption, SystemEffectOption, FailureInstanceOption,
  SafetyMechanismOption, CoverageLink, ThermalModelOption, StandardOption,
  IdSuggestions
} from '../models';
import { TimelineJournalEvent } from '../../../websocket.service';

@Injectable({
  providedIn: 'root'
})
export class StoreService {
  readonly searchQuery = signal<string>('');

  private static readonly CURRENT_PROJECT_STORAGE_KEY = 'fmeda.current.project.v1';
  readonly currentProject = signal<{ id: string; name: string } | null>(this.loadCurrentProject());

  readonly components = signal<ComponentSummary[]>([]);
  readonly selectedComponent = signal<ComponentDetail | null>(null);
  readonly componentTypes = signal<ComponentType[]>([]);
  readonly safetyGoals = signal<SafetyGoal[]>([]);
  readonly analysis = signal<AnalysisRow[]>([]);
  readonly failureEffects = signal<FailureEffectOption[]>([]);
  readonly systemEffects = signal<SystemEffectOption[]>([]);
  readonly failureInstances = signal<FailureInstanceOption[]>([]);
  readonly safetyMechanisms = signal<SafetyMechanismOption[]>([]);
  readonly coverageLinks = signal<CoverageLink[]>([]);
  readonly thermalModels = signal<ThermalModelOption[]>([]);
  readonly standards = signal<StandardOption[]>([]);
  readonly idSuggestions = signal<IdSuggestions>({
    safety_goal_id: 'SG-001',
    system_effect_id: 'SE-001',
    failure_effect_id: 'FE-001',
    failure_instance_id: 'FI-001',
    safety_mechanism_id: 'SM-001'
  });
  readonly selectedWorksheetRowId = signal<string>('');

  readonly isUndoing = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  readonly error = signal<string>('');
  readonly actionMessage = signal<string>('');
  readonly timelineEvents = signal<TimelineJournalEvent[]>([]);

  constructor(private apiService: ApiService) { }
  /**
   * Triggers a project-level timeline undo and executes a refresh callback
   */
  triggerUndo(onSuccessRefresh?: () => void): void {
    if (this.isUndoing()) return;

    this.isUndoing.set(true);
    this.error.set(''); // Clear any stale errors

    this.apiService.undoLastAction().subscribe({
      next: (res) => {
        const undoneAction = res?.data?.action_undone || 'Action';
        this.actionMessage.set(`Undone: ${undoneAction}`);
        this.isUndoing.set(false);

        // Call the data refresh routine (e.g. from BusinessLogicService)
        if (onSuccessRefresh) {
          onSuccessRefresh();
        }
      },
      error: (err) => {
        const errorMessage = err?.error?.message || 'Failed to undo action or history empty.';
        this.error.set(errorMessage);
        this.isUndoing.set(false);
      }
    });
  }
  setCurrentProject(project: { id: string; name: string } | null) {
    this.currentProject.set(project);
    try {
      if (project) {
        localStorage.setItem(StoreService.CURRENT_PROJECT_STORAGE_KEY, JSON.stringify(project));
      } else {
        localStorage.removeItem(StoreService.CURRENT_PROJECT_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures (e.g. private browsing); in-memory state still applies.
    }
  }

  private loadCurrentProject(): { id: string; name: string } | null {
    try {
      const raw = localStorage.getItem(StoreService.CURRENT_PROJECT_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.id === 'string' && typeof parsed.name === 'string') {
        return { id: parsed.id, name: parsed.name };
      }
    } catch {
      // Ignore malformed storage values.
    }
    return null;
  }

  /**
   * Prepends a new event received from STOMP to the live timeline feed
   */
  addTimelineEvent(event: TimelineJournalEvent): void {
    if (!event) return;
    this.timelineEvents.update((events) => [event, ...events]);
  }

  /**
   * Sets initial timeline history array fetched via REST API
   */
  setTimelineEvents(events: TimelineJournalEvent[]): void {
    this.timelineEvents.set(events || []);
  }
}
